import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, Subject, takeUntil } from 'rxjs';
import { IPlanCategory } from '@gray/domain/models/plan/plan-category.model';
import { IPlan } from '@gray/domain/models/plan/plan.model';
import { IPlanWeek } from '@gray/domain/models/plan/plan-week.model';
import { CoachStudentPlanService } from '../../../../core/services/coach-student-plan.service';

type TStep = 'category' | 'plan' | 'week';

/**
 * Loads a ready-made plan onto the student's planner: pick a category, a plan, and the week it
 * should start from. Every item of the plan is imported — the student's own modal lets them
 * cherry-pick items, which the coach panel can grow into later.
 */
@Component({
  selector: 'coach-planner-plan-modal',
  templateUrl: './planner-plan-modal.component.html',
  styleUrl: './planner-plan-modal.component.scss',
})
export class PlannerPlanModalComponent implements OnInit, OnDestroy {
  @Input() studentId!: number;
  @Input() periodId!: number;
  @Input() weeks: IPlanWeek[] = [];

  step: TStep = 'category';
  categories: IPlanCategory[] = [];
  selectedCategory?: IPlanCategory;
  selectedPlan?: IPlan;

  isLoading = false;
  isImporting = false;

  private itemIds: number[] = [];

  destroyed$ = new Subject();

  constructor(
    public modal: NgbActiveModal,
    private planService: CoachStudentPlanService,
  ) {
  }

  ngOnInit(): void {
    this.getCategories();
  }

  getCategories() {
    this.isLoading = true;
    this.planService.getPlanCategories(this.studentId, this.periodId)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        this.categories = (r.data ?? []) as IPlanCategory[];
      });
  }

  selectCategory(category: IPlanCategory) {
    this.selectedCategory = category;
    this.step = 'plan';
  }

  /** The plan's items are collected here so the week step can import them in one call. */
  selectPlan(plan: IPlan) {
    this.selectedPlan = plan;

    this.isLoading = true;
    this.planService.getPlanDetails(this.studentId, plan.id, this.periodId)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        const details = r.data;
        const planGroup = details?.planGroups?.[0];
        this.itemIds = (planGroup?.planGroupWeeks ?? [])
          .flatMap((week: any) => week.planGroupWeekItems ?? [])
          .map((item: any) => item.id);

        this.step = 'week';
      });
  }

  importToWeek(week: IPlanWeek) {
    if (!this.selectedPlan || !this.itemIds.length) {
      return;
    }

    this.isImporting = true;
    this.planService
      .importPlan(this.studentId, this.selectedPlan.id, {startWeek: week.weekNumber, itemIds: this.itemIds}, this.periodId)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isImporting = false))
      .subscribe(() => this.modal.close(true));
  }

  back() {
    if (this.step === 'week') {
      this.step = 'plan';
      this.selectedPlan = undefined;
      return;
    }

    this.step = 'category';
    this.selectedCategory = undefined;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
